/**
 * Community Posts Mutation Hooks
 *
 * TanStack Query mutation hooks for post actions
 * with optimistic updates and cache invalidation
 */

import {
  useMutation,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
  type QueryKey,
} from '@tanstack/react-query';
import { toast } from 'sonner';
import { postsClient, type CreatePostParams, type UpdatePostParams } from '../client/posts.client';
import { isPostListQueryKey, queryKeys } from '../query-keys';
import type { PaginatedResponse, Post } from '../types';
import { captureError } from '@/shared/lib/errors/capture';

// ─────────────────────────────────────────────────────────────────────────────
// Shape guards & helpers
// ─────────────────────────────────────────────────────────────────────────────

type PostListCache = InfiniteData<PaginatedResponse<Post>> | PaginatedResponse<Post> | undefined;

type PostPatch = Partial<
  Pick<Post, 'has_liked' | 'has_disliked' | 'likes_count' | 'dislikes_count'>
>;

type PostTransformer = (post: Post) => Post;

function isInfinite(data: unknown): data is InfiniteData<PaginatedResponse<Post>> {
  return !!data && typeof data === 'object' && 'pages' in (data as Record<string, unknown>);
}

function isPaginated(data: unknown): data is PaginatedResponse<Post> {
  return (
    !!data &&
    typeof data === 'object' &&
    'data' in (data as Record<string, unknown>) &&
    Array.isArray((data as { data: unknown }).data)
  );
}

/**
 * Apply `transform` to the post with matching `postId` inside a flat paginated
 * page. Returns the same `data` reference when no post matched (preserves
 * referential identity for unchanged pages).
 */
function applyTransformerToPaginated(
  data: PaginatedResponse<Post>,
  postId: number,
  transform: PostTransformer
): { next: PaginatedResponse<Post>; mutated: boolean } {
  let mutated = false;
  const nextRows = data.data.map((p) => {
    if (p.id !== postId) return p;
    mutated = true;
    return transform(p);
  });
  if (!mutated) return { next: data, mutated };
  return { next: { ...data, data: nextRows }, mutated };
}

/**
 * Apply `transform` to every page in an infinite cache. Preserves the original
 * page reference when that page contained no matching post, and the original
 * wrapper when no page was mutated at all.
 */
function applyTransformerToInfinite(
  data: InfiniteData<PaginatedResponse<Post>>,
  postId: number,
  transform: PostTransformer
): InfiniteData<PaginatedResponse<Post>> {
  let anyMutated = false;
  const nextPages = data.pages.map((page) => {
    if (!page || !Array.isArray(page.data)) return page;
    const { next, mutated } = applyTransformerToPaginated(page, postId, transform);
    if (mutated) anyMutated = true;
    return next;
  });
  if (!anyMutated) return data;
  return { ...data, pages: nextPages };
}

/**
 * Remove the post with matching `postId` from a flat paginated page.
 * Returns the same reference when the post wasn't present.
 */
function removePostFromPaginated(
  data: PaginatedResponse<Post>,
  postId: number
): { next: PaginatedResponse<Post>; mutated: boolean } {
  const filtered = data.data.filter((p) => p.id !== postId);
  if (filtered.length === data.data.length) return { next: data, mutated: false };
  return { next: { ...data, data: filtered }, mutated: true };
}

/**
 * Remove `postId` from every page of an infinite cache.
 */
function removePostFromInfinite(
  data: InfiniteData<PaginatedResponse<Post>>,
  postId: number
): InfiniteData<PaginatedResponse<Post>> {
  let anyMutated = false;
  const nextPages = data.pages.map((page) => {
    if (!page || !Array.isArray(page.data)) return page;
    const { next, mutated } = removePostFromPaginated(page, postId);
    if (mutated) anyMutated = true;
    return next;
  });
  if (!anyMutated) return data;
  return { ...data, pages: nextPages };
}

/**
 * Snapshot every list cache whose key matches `isPostListQueryKey`. Returns a
 * tuple of [key, data] pairs suitable for rollback via `setQueryData(key, data)`.
 */
function snapshotPostListCaches(queryClient: QueryClient): [QueryKey, PostListCache][] {
  const entries = queryClient.getQueriesData<PostListCache>({
    predicate: (q) => isPostListQueryKey(q.queryKey),
  });
  return entries.map(([key, data]) => [key, data]);
}

/**
 * Run `transform` against the post matching `postId` in every list cache.
 * Preserves referential identity of non-matching rows, pages, and wrappers.
 */
function transformPostInAllLists(
  queryClient: QueryClient,
  postId: number,
  transform: PostTransformer
): void {
  queryClient.setQueriesData<PostListCache>(
    { predicate: (q) => isPostListQueryKey(q.queryKey) },
    (data) => {
      if (isInfinite(data)) return applyTransformerToInfinite(data, postId, transform);
      if (isPaginated(data)) {
        const { next } = applyTransformerToPaginated(data, postId, transform);
        return next;
      }
      return data;
    }
  );
}

/**
 * Apply a static `patch` to every post matching `postId` in every list cache.
 * Thin wrapper over `transformPostInAllLists` for reconcile paths that merge
 * server-authoritative fields.
 */
function patchPostInAllLists(queryClient: QueryClient, postId: number, patch: PostPatch): void {
  transformPostInAllLists(queryClient, postId, (p) => ({ ...p, ...patch }));
}

/**
 * Remove `postId` from every list cache.
 */
function removePostFromAllLists(queryClient: QueryClient, postId: number): void {
  queryClient.setQueriesData<PostListCache>(
    { predicate: (q) => isPostListQueryKey(q.queryKey) },
    (data) => {
      if (isInfinite(data)) return removePostFromInfinite(data, postId);
      if (isPaginated(data)) {
        const { next } = removePostFromPaginated(data, postId);
        return next;
      }
      return data;
    }
  );
}

/**
 * Restore every snapshotted list cache to its pre-mutation value.
 */
function restorePostListCaches(
  queryClient: QueryClient,
  snapshot: [QueryKey, PostListCache][]
): void {
  for (const [key, data] of snapshot) {
    queryClient.setQueryData(key, data);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Mutations
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Hook to update an existing post
 */
export function useUpdatePost() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ uuid, params }: { uuid: string; params: UpdatePostParams }) =>
      postsClient.update(uuid, params),
    onSuccess: (_data, { uuid }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.community.postDetailByUuid(uuid) });
      queryClient.invalidateQueries({ queryKey: queryKeys.community.posts() });
      queryClient.invalidateQueries({ queryKey: ['studio', 'posts'] });
    },
  });
}

/**
 * Hook to create a new post
 */
export function useCreatePost() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: CreatePostParams) => postsClient.create(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.community.postList() });
    },
  });
}

/**
 * Hook to like a post with optimistic update propagated across every
 * community / creator-profile list cache that renders the post, plus the
 * single-post detail cache. Rolls back all caches on error and reconciles
 * to server-returned counts on success.
 */
export function useLikePost() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (postId: number) => postsClient.like(postId),

    onMutate: async (postId) => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: queryKeys.community.postDetail(postId) }),
        queryClient.cancelQueries({ predicate: (q) => isPostListQueryKey(q.queryKey) }),
      ]);

      const previousDetail = queryClient.getQueryData<Post>(queryKeys.community.postDetail(postId));
      const previousLists = snapshotPostListCaches(queryClient);

      if (previousDetail) {
        const nextLiked = !previousDetail.has_liked;
        queryClient.setQueryData<Post>(queryKeys.community.postDetail(postId), {
          ...previousDetail,
          has_liked: nextLiked,
          likes_count: previousDetail.likes_count + (nextLiked ? 1 : -1),
        });
      }

      transformPostInAllLists(queryClient, postId, (p) => {
        const nextLiked = !p.has_liked;
        return {
          ...p,
          has_liked: nextLiked,
          likes_count: p.likes_count + (nextLiked ? 1 : -1),
        };
      });

      return { previousDetail, previousLists };
    },

    onSuccess: (data, postId) => {
      const cachedDetail = queryClient.getQueryData<Post>(queryKeys.community.postDetail(postId));
      if (cachedDetail) {
        queryClient.setQueryData<Post>(queryKeys.community.postDetail(postId), {
          ...cachedDetail,
          likes_count: data.likes_count,
          dislikes_count: data.dislikes_count,
        });
      }

      patchPostInAllLists(queryClient, postId, {
        likes_count: data.likes_count,
        dislikes_count: data.dislikes_count,
      });
    },

    onError: (err, postId, context) => {
      if (context?.previousDetail) {
        queryClient.setQueryData(queryKeys.community.postDetail(postId), context.previousDetail);
      }
      if (context?.previousLists) {
        restorePostListCaches(queryClient, context.previousLists);
      }

      toast.error('Failed to like post');
      captureError(err, {
        component: 'useLikePost',
        action: 'mutate',
        severity: 'warning',
        tags: { signal: 'noise' },
      });
    },

    onSettled: (_data, _error, postId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.community.postDetail(postId) });
      queryClient.invalidateQueries({
        predicate: (q) => isPostListQueryKey(q.queryKey),
        refetchType: 'none',
      });
    },
  });
}

/**
 * Hook to dislike a post. Same cross-cache semantics as `useLikePost`.
 */
export function useDislikePost() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (postId: number) => postsClient.dislike(postId),

    onMutate: async (postId) => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: queryKeys.community.postDetail(postId) }),
        queryClient.cancelQueries({ predicate: (q) => isPostListQueryKey(q.queryKey) }),
      ]);

      const previousDetail = queryClient.getQueryData<Post>(queryKeys.community.postDetail(postId));
      const previousLists = snapshotPostListCaches(queryClient);

      if (previousDetail) {
        const nextDisliked = !previousDetail.has_disliked;
        queryClient.setQueryData<Post>(queryKeys.community.postDetail(postId), {
          ...previousDetail,
          has_disliked: nextDisliked,
          dislikes_count: previousDetail.dislikes_count + (nextDisliked ? 1 : -1),
        });
      }

      transformPostInAllLists(queryClient, postId, (p) => {
        const nextDisliked = !p.has_disliked;
        return {
          ...p,
          has_disliked: nextDisliked,
          dislikes_count: p.dislikes_count + (nextDisliked ? 1 : -1),
        };
      });

      return { previousDetail, previousLists };
    },

    onSuccess: (data, postId) => {
      const cachedDetail = queryClient.getQueryData<Post>(queryKeys.community.postDetail(postId));
      if (cachedDetail) {
        queryClient.setQueryData<Post>(queryKeys.community.postDetail(postId), {
          ...cachedDetail,
          likes_count: data.likes_count,
          dislikes_count: data.dislikes_count,
        });
      }

      patchPostInAllLists(queryClient, postId, {
        likes_count: data.likes_count,
        dislikes_count: data.dislikes_count,
      });
    },

    onError: (err, postId, context) => {
      if (context?.previousDetail) {
        queryClient.setQueryData(queryKeys.community.postDetail(postId), context.previousDetail);
      }
      if (context?.previousLists) {
        restorePostListCaches(queryClient, context.previousLists);
      }

      toast.error('Failed to dislike post');
      captureError(err, {
        component: 'useDislikePost',
        action: 'mutate',
        severity: 'warning',
        tags: { signal: 'noise' },
      });
    },

    onSettled: (_data, _error, postId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.community.postDetail(postId) });
      queryClient.invalidateQueries({
        predicate: (q) => isPostListQueryKey(q.queryKey),
        refetchType: 'none',
      });
    },
  });
}

/**
 * Hook to delete a post. Removes the post optimistically from every matching
 * list cache; rolls back on error.
 */
export function useDeletePost() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (postId: number) => postsClient.delete(postId),

    onMutate: async (postId) => {
      await queryClient.cancelQueries({ predicate: (q) => isPostListQueryKey(q.queryKey) });

      const previousLists = snapshotPostListCaches(queryClient);
      removePostFromAllLists(queryClient, postId);

      return { previousLists };
    },

    onError: (err, _postId, context) => {
      if (context?.previousLists) {
        restorePostListCaches(queryClient, context.previousLists);
      }

      toast.error('Failed to delete post');
      captureError(err, {
        component: 'useDeletePost',
        action: 'mutate',
        severity: 'warning',
        tags: { signal: 'noise' },
      });
    },

    onSettled: (_data, error, postId) => {
      // On success the post no longer exists — drop the detail cache.
      // On error the rollback restored lists; still invalidate to re-sync.
      if (!error) {
        queryClient.removeQueries({ queryKey: queryKeys.community.postDetail(postId) });
      } else {
        queryClient.invalidateQueries({ queryKey: queryKeys.community.postDetail(postId) });
      }
      queryClient.invalidateQueries({
        predicate: (q) => isPostListQueryKey(q.queryKey),
        refetchType: 'none',
      });
    },
  });
}

/**
 * Hook to delete a comment on a post
 */
export function useDeletePostComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ commentUuid }: { commentUuid: string; postId: number }) =>
      postsClient.deleteComment(commentUuid),
    onSuccess: (_data, { postId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.community.comments(postId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.community.postDetail(postId) });
    },
  });
}

/**
 * Hook to like/unlike a comment on a post
 */
export function useLikePostComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ commentId }: { commentId: number; postId: number }) =>
      postsClient.likeComment(commentId),
    onSuccess: (_data, { postId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.community.comments(postId) });
    },
  });
}

export function useAddPostComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      postId,
      content,
      parentId,
    }: {
      postId: number;
      content: string;
      parentId?: number;
    }) => postsClient.addComment(postId, content, parentId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.community.comments(variables.postId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.community.postDetail(variables.postId) });
    },
  });
}
